import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect } from "react";
import type {
  IActivitySchedule,
  IRescheduleActivity,
  ITime,
} from "@/api/Activities";
import { ANY_TIME_OF_DAY, rescheduleActivity } from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { FormProvider, useForm } from "react-hook-form";
import type { IRecurrencePickerFields } from "@/forms/AddActivityForm/Scheduling/RecurrencePicker";
import { RecurrencePicker } from "@/forms/AddActivityForm/Scheduling/RecurrencePicker";
import {
  customRecurrenceIntervalUnitSchema,
  recurringNumberDictionary,
  recurringOptionSchema,
} from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import type { ITimeSlotsPickerFields } from "@/forms/AddActivityForm/Scheduling/TimeSlotsPicker";
import { TimeSlotsPicker } from "@/forms/AddActivityForm/Scheduling/TimeSlotsPicker";
import {
  recurrencesPerDayOptionSchema,
  timespanToHours,
} from "@/forms/AddActivityForm/Scheduling/timeSlotsUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import type { IWeekdaysPickerFields } from "@/forms/AddActivityForm/Scheduling/WeekdaysPicker";
import { WeekdaysPicker } from "@/forms/AddActivityForm/Scheduling/WeekdaysPicker";
import {
  getTimeFields,
  getTimespan,
} from "@/forms/AddActivityForm/activityTimeUtils";
import { timeOfDaySchema } from "@medoma/models/activities";
import * as Sentry from "@sentry/react";
import {
  CustomRecurringInterval,
  type ICustomRecurringIntervalFields,
} from "@/forms/AddActivityForm/Scheduling/CustomRecurringInput";
import type { ITimeSensitivityFields } from "@/forms/AddActivityForm/Scheduling/TimeSensitivityPicker";
import { useDialog } from "@medoma/components/Dialog/useDialog";

type IEditScheduleFields = IRecurrencePickerFields &
  IWeekdaysPickerFields &
  ICustomRecurringIntervalFields &
  ITimeSlotsPickerFields &
  ITimeSensitivityFields;

export const EditSchedule = ({
  activityId,
  schedule,
  onSuccess,
}: {
  activityId: string;
  schedule: IActivitySchedule;
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const scheduleTypeIsRecurring = "interval" in schedule || "days" in schedule;
  const scheduleTypeIsInterval = "interval" in schedule;
  const scheduleTypeIsWeekdays = "days" in schedule;

  const intervalAsWeeksOrDays = (interval: number) => {
    if (interval % 7 === 0 && interval >= 7) {
      return {
        number: interval / 7,
        unit: customRecurrenceIntervalUnitSchema.enum.weeks,
      };
    }
    return {
      number: interval,
      unit: customRecurrenceIntervalUnitSchema.enum.days,
    };
  };

  const times = scheduleTypeIsRecurring ? schedule.times : [];
  const isAnyTimeOfDay = times === ANY_TIME_OF_DAY;
  const hasTimeslots = !isAnyTimeOfDay && times.length > 0;

  const timeSensitivity = hasTimeslots
    ? timespanToHours(schedule.span)
    : undefined;

  const methods = useForm<IEditScheduleFields>({
    defaultValues: {
      recurrence: scheduleTypeIsWeekdays
        ? recurringOptionSchema.enum.onSelectedWeekdays
        : scheduleTypeIsInterval
          ? (recurringNumberDictionary[schedule.interval] ??
            recurringOptionSchema.enum.custom)
          : null,
      customRecurrenceInterval: scheduleTypeIsInterval
        ? intervalAsWeeksOrDays(schedule.interval).number.toString()
        : null,
      customRecurrenceIntervalUnit: scheduleTypeIsInterval
        ? intervalAsWeeksOrDays(schedule.interval).unit
        : null,
      timeCategory: isAnyTimeOfDay
        ? timeOfDaySchema.enum.Any
        : timeOfDaySchema.enum.Specific,
      recurrencesPerDay: hasTimeslots
        ? recurrencesPerDayOptionSchema.parse(times.length.toString())
        : "1",
      timeslots: hasTimeslots ? times.map((time: ITime) => ({ time })) : [],
      timeSensitivity,
      weekdays: scheduleTypeIsWeekdays ? schedule.days : [],
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
    unregister,
    watch,
  } = methods;

  const queryClient = useQueryClient();
  const { confirm } = useDialog();
  const { mutate, isPending } = useMutation({
    mutationFn: (formData: IRescheduleActivity) =>
      rescheduleActivity(activityId, formData, confirm),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async (reschedulingWasPerformed) => {
      if (!reschedulingWasPerformed) {
        return;
      }

      await queryClient.invalidateQueries();

      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((validatedFormData) => {
    const {
      recurrence,
      timeCategory,
      timeSensitivity,
      timeslots,
      weekdays,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit,
    } = validatedFormData;

    if (recurrence === null) {
      Sentry.captureException("Failed to edit schedule. Recurrence is null.");
      return;
    }

    const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;

    const isRecurringAsInterval =
      recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

    const recurringTimeOfDayFields = isAnyTimeOfDay
      ? {
          isAnyTimeOfDay: true as const,
          timeslots: undefined,
        }
      : {
          isAnyTimeOfDay: false as const,
          timeslots: timeslots as {
            time: string;
          }[],
        };

    const rescheduleActivityFields: IRescheduleActivity = {
      span: getTimespan({ isAnyTimeOfDay, timeSensitivity }),
      ...getTimeFields({
        isRecurringAsInterval,
        weekdays,
        recurrence,
        customRecurrenceInterval,
        customRecurrenceIntervalUnit,
        ...recurringTimeOfDayFields,
      }),
    };

    mutate(rescheduleActivityFields);
  });

  const isRecurringOnWeekdays =
    // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
    // oxlint-disable-next-line react/incompatible-library
    watch("recurrence") === recurringOptionSchema.enum.onSelectedWeekdays;

  const isCustomRecurring =
    watch("recurrence") === recurringOptionSchema.enum.custom;

  // Unregister the weekdays field if the activity is not recurring on weekdays
  useEffect(() => {
    if (!isRecurringOnWeekdays) {
      unregister("weekdays");
    }
  }, [isRecurringOnWeekdays, unregister]);

  // Unregister the custom recurring interval field if the activity is not custom recurring
  useEffect(() => {
    if (!isCustomRecurring) {
      unregister("customRecurrenceInterval");
      unregister("customRecurrenceIntervalUnit");
    }
  }, [isCustomRecurring, unregister]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Ändrar schemat`} />
        ) : errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} />
        ) : (
          <></>
        )}
        <RecurrencePicker />
        {isRecurringOnWeekdays ? <WeekdaysPicker /> : <></>}
        {isCustomRecurring ? <CustomRecurringInterval /> : <></>}

        <TimeSlotsPicker isRecurring />
        {isDirty ? (
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        ) : null}
      </Form>
    </FormProvider>
  );
};
